// Shared data plumbing for the Coaching Portal pages (Coaching Dashboard,
// Flagged Drivers, Recognition Candidates): acting-tenant resolution, the
// tickets + scorecard fetches, flag/recognition-candidate computation, the
// per-driver ticket-history join, and the Assign / Recognize modal flows.
// Extracted when the flagged/recognition tables moved to their own pages so
// three components can never drift on how a flag or a "queued" badge is
// computed. Pages add their own extras (the dashboard adds effectiveness,
// the table pages add sort/filter/pagination state).
//
// Hooks a consuming page may define:
//   onTenantScopeReset()   — extra state to clear when the acting tenant
//                            changes (the mixin already clears its own and
//                            reloads tickets + scorecards)
//   afterTicketMutation()  — extra refresh after an Assign/Recognize create
import { mapState } from 'vuex'
import moment from 'moment'
import { get_all_driver_scorecards } from '../networking/tbn_apis'
import {
  cm_get_all_driver_scorecards,
  get_coaching_tickets, create_coaching_ticket, get_contact_users,
} from '../networking/apis'
import { mergeThresholds } from '../utils/driver_manager_thresholds'
import { buildBadnessPools, getDriverFlags, getRecognitionCandidates, isActiveDriver } from '../utils/scorecard_flags'
import { defaultScorecardRange } from '../utils/driver_manager_dates'
import { resolveActingTenant, tenantDisplayName } from '../utils/tenant_context'

export const OPEN_STATUSES = ['open', 'scheduled']

export default {
  data() {
    return {
      loading: false,
      error: null,
      tickets: [],
      summary: {},
      settings: null,

      flaggedLoading: false,
      flaggedError: null,
      flaggedDrivers: [],
      recognitionCandidates: [],
      scorecardRange: null,
      // page-set month overrides (YYYY-MM) for the scorecard window; empty
      // means the shared default range. every flag and recognition candidate
      // is computed over this window, so changing it re-queries.
      scorecardFrom: '',
      scorecardTo: '',

      assignDriver: null,
      assignFlagKey: null,
      // sentinel radio value for a hand-typed topic: the flag list covers
      // scorecard metrics, but a supervisor sometimes needs to coach on
      // something the scorecard doesn't measure
      ASSIGN_CUSTOM: '__custom__',
      assignCustomTopic: '',
      assignWhy: '',
      assignWhyPristine: '',
      assignDueDate: '',
      assignSaving: false,
      // who the ticket lands on. defaults to the person assigning, but a
      // supervisor raising flags for the fleet usually isn't the one who
      // will hold the conversation (evan, 2026-08-17).
      assignTo: null,
      contacts: [],
      contactsLoaded: false,

      recognizeDriver: null,
      recognizeReasonKey: null,
      recognizeWhy: '',
      recognizeWhyPristine: '',
      recognizeSaving: false,
      // who delivers the recognition — same principle as assignTo: the
      // person spotting the numbers isn't always the one who says it
      recognizeTo: null,
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    tenant() {
      return resolveActingTenant(this.user, this.$route.query.tenant)
    },
    // ops-family convention (utils/tenant_context.js): whenever the page
    // renders a different tenant than the navbar selection, say so
    actingTenantDiffers() {
      return String(this.tenant) !== String(this.user.tenant)
    },
    actingTenantLabel() {
      return tenantDisplayName(this.user, this.tenant)
    },
    isCmTenant() {
      return (this.user && this.user.types && this.user.types[this.tenant]) === 'CM'
    },
    coachingTickets() {
      return this.tickets.filter(t => t.kind !== 'recognition')
    },
    recognitionTickets() {
      return this.tickets.filter(t => t.kind === 'recognition')
    },
    recognitionSummary() {
      return (this.summary && this.summary.recognition) || { open: 0, scheduled: 0, completed: 0 }
    },
    // "driverKey#topic" for every open ticket, keyed by BOTH identifiers to
    // mirror the backend's dual-partition dedup rule
    openTicketKeys() {
      const keys = new Set()
      this.coachingTickets.filter(t => OPEN_STATUSES.includes(t.status)).forEach(t => {
        if (t.driver_id) keys.add(`${t.driver_id}#${t.topic}`)
        if (t.driver_name) keys.add(`${t.driver_name}#${t.topic}`)
      })
      return keys
    },
    openRecognitionKeys() {
      const keys = new Set()
      this.recognitionTickets.filter(t => OPEN_STATUSES.includes(t.status)).forEach(t => {
        if (t.driver_id) keys.add(`${t.driver_id}#${t.topic}`)
        if (t.driver_name) keys.add(`${t.driver_name}#${t.topic}`)
      })
      return keys
    },
    // the assignee picker. mirrors the sign-off modal's reassign list:
    // whoever the contacts feed offers (it already excludes drivers), with
    // the acting user first so the common case is one glance.
    assignContactOptions() {
      const me = this.user.username
      const options = this.contacts
        .filter(c => c.username !== me)
        .map(c => ({
          value: c.username,
          text: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.username,
        }))
      return [
        { value: me, text: `${this.user.full_name || me} (me)` },
        ...options,
      ]
    },
    // the Assign button's gate: a flag radio is enough on its own, the
    // custom radio also needs its topic typed
    canSaveAssign() {
      if (this.assignFlagKey === this.ASSIGN_CUSTOM) return !!this.assignCustomTopic.trim()
      return !!this.assignFlagKey
    },
    scorecardPeriodLabel() {
      if (!this.scorecardRange) return '—'
      const from = moment(this.scorecardRange.startMonth, 'YYYY-MM').format('MMM YYYY')
      const to = moment(this.scorecardRange.endMonth, 'YYYY-MM').format('MMM YYYY')
      return from === to ? from : `${from} – ${to}`
    },
    // stored on the ticket so the reason it was raised survives the
    // scorecard moving on: "2026-02..2026-07"
    scorecardPeriodValue() {
      return this.scorecardRange
        ? `${this.scorecardRange.startMonth}..${this.scorecardRange.endMonth}` : null
    },
    // ticket history joined per driver, keyed by BOTH driver_id and
    // driver_name (tickets and scorecard rows may carry either)
    ticketStatsByDriver() {
      const stats = {}
      const rowFor = key => {
        if (!key) return null
        return stats[key] = stats[key] || { lastCoached: null, lastRecognized: null, openCoaching: 0 }
      }
      const touch = (ticket, fn) => {
        const seen = new Set()
        ;[ticket.driver_id, ticket.driver_name].forEach(k => {
          const key = k && String(k)
          if (!key || seen.has(key)) return
          seen.add(key)
          const row = rowFor(key)
          if (row) fn(row)
        })
      }
      this.tickets.forEach(t => {
        const isRecognition = t.kind === 'recognition'
        if (!isRecognition && OPEN_STATUSES.includes(t.status)) {
          touch(t, row => { row.openCoaching += 1 })
        }
        if (t.completed_at) {
          touch(t, row => {
            const field = isRecognition ? 'lastRecognized' : 'lastCoached'
            if (!row[field] || t.completed_at > row[field]) row[field] = t.completed_at
          })
        }
      })
      return stats
    },
  },
  watch: {
    // the navbar tenant switcher mutates user.tenant in place without
    // remounting the page (ops_desk.vue precedent): on any tenant change,
    // close the create modals and drop every derived row before an Assign
    // could write the old tenant's driver into the new tenant's queue.
    tenant() {
      this.$bvModal.hide('cs-assign-modal')
      this.$bvModal.hide('cs-recognize-modal')
      this.assignDriver = null
      this.recognizeDriver = null
      this.assignTo = null
      this.recognizeTo = null
      this.contacts = []
      this.contactsLoaded = false
      this.tickets = []
      this.summary = {}
      this.settings = null
      this.flaggedDrivers = []
      this.recognitionCandidates = []
      this.scorecardRange = null
      this.scorecardFrom = ''
      this.scorecardTo = ''
      if (this.onTenantScopeReset) this.onTenantScopeReset()
      this.loadTickets()
      this.loadFlaggedDrivers()
    },
    // the Why/Note prefill follows the selected radio, but never clobbers a
    // narrative the supervisor already customized
    assignFlagKey(key) {
      if (!this.assignDriver) return
      if (key === this.ASSIGN_CUSTOM) {
        // a custom topic has no flag note to offer; clear an untouched
        // prefill so the leftover metric text can't ride along as the Why
        if (this.assignWhy.trim() === '' || this.assignWhy === this.assignWhyPristine) {
          this.assignWhy = ''
          this.assignWhyPristine = ''
        }
        return
      }
      const flag = this.assignDriver.flags.find(f => f.key === key)
      if (!flag) return
      if (this.assignWhy.trim() === '' || this.assignWhy === this.assignWhyPristine) {
        this.assignWhy = flag.note
        this.assignWhyPristine = flag.note
      }
    },
    recognizeReasonKey(key) {
      if (!this.recognizeDriver) return
      const reason = this.recognizeDriver.reasons.find(r => r.key === key)
      if (!reason) return
      if (this.recognizeWhy.trim() === '' || this.recognizeWhy === this.recognizeWhyPristine) {
        this.recognizeWhy = reason.note
        this.recognizeWhyPristine = reason.note
      }
    },
  },
  created() {
    this.loadTickets()
    this.loadFlaggedDrivers()
  },
  methods: {
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback
    },
    // manual sort helper for the table pages (they are no-local-sorting
    // because b-table would only sort the visible page). numbers compare
    // numerically with nulls last; everything else compares as strings.
    sortRows(rows, sortBy, sortDesc) {
      if (!sortBy) return rows
      const dir = sortDesc ? -1 : 1
      return [...rows].sort((a, b) => {
        const av = a[sortBy]
        const bv = b[sortBy]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
    },

    async loadTickets() {
      this.loading = true
      this.error = null
      try {
        const resp = await get_coaching_tickets(this.tenant)
        const payload = (resp.value && resp.value.data) || {}
        this.tickets = payload.tickets || []
        this.summary = payload.summary || {}
        if (payload.settings) this.settings = payload.settings
      } catch (e) {
        this.error = this.apiErrorMessage(e, 'Failed to load the coaching data.')
      } finally {
        this.loading = false
      }
    },
    async loadFlaggedDrivers() {
      this.flaggedLoading = true
      this.flaggedError = null
      try {
        // the same default window as the scorecard dashboard, via the shared
        // helper — every coaching surface computes flags over identical
        // months. pages may override either end (Period filters); a
        // backwards pair is swapped rather than erroring.
        const range = defaultScorecardRange()
        let startMonth = this.scorecardFrom || range.from.format('YYYY-MM')
        let endMonth = this.scorecardTo || range.to.format('YYYY-MM')
        if (startMonth > endMonth) [startMonth, endMonth] = [endMonth, startMonth]
        const fetcher = this.isCmTenant ? cm_get_all_driver_scorecards : get_all_driver_scorecards
        const resp = await fetcher(this.tenant, startMonth, endMonth)
        const payload = (resp.value && resp.value.data) || {}
        const drivers = payload.drivers || []
        const thresholds = mergeThresholds(payload.scorecard_thresholds)
        const activeDrivers = drivers.filter(d => isActiveDriver(d))
        const pools = buildBadnessPools(activeDrivers)
        this.scorecardRange = { startMonth, endMonth }
        this.flaggedDrivers = drivers
          .map(d => ({
            key: d.driver_id || d.driver_name,
            driver: d,
            flags: getDriverFlags(d, { pools, activeDrivers, thresholds }),
          }))
          .filter(fd => fd.flags.length)
          .sort((a, b) => b.flags.length - a.flags.length)
        this.recognitionCandidates = getRecognitionCandidates(activeDrivers, thresholds)
      } catch (e) {
        this.flaggedError = this.apiErrorMessage(e, 'Could not load scorecard flags for this period.')
      } finally {
        this.flaggedLoading = false
      }
    },

    isQueued(driver, topic) {
      return (!!driver.driver_id && this.openTicketKeys.has(`${driver.driver_id}#${topic}`))
        || (!!driver.driver_name && this.openTicketKeys.has(`${driver.driver_name}#${topic}`))
    },
    allFlagsQueued(fd) {
      return fd.flags.every(f => this.isQueued(fd.driver, f.label))
    },
    async loadContacts() {
      if (this.contactsLoaded) return
      this.contactsLoaded = true
      try {
        const resp = await get_contact_users(this.tenant)
        this.contacts = ((resp.value && resp.value.data) || {}).results || []
      } catch (e) {
        // the picker degrades to "me"; retry on the next open
        this.contactsLoaded = false
      }
    },
    openAssign(fd) {
      this.assignDriver = fd
      this.assignCustomTopic = ''
      const firstFree = fd.flags.find(f => !this.isQueued(fd.driver, f.label)) || fd.flags[0]
      this.assignFlagKey = firstFree ? firstFree.key : null
      this.assignWhy = firstFree ? firstFree.note : ''
      this.assignWhyPristine = this.assignWhy
      // 5 days by default, from the tenant's own SLA setting
      const dueDays = (this.settings && this.settings.due_days_default) || 5
      this.assignDueDate = moment().add(dueDays, 'days').format('YYYY-MM-DD')
      this.assignTo = this.user.username
      this.loadContacts()
      this.$bvModal.show('cs-assign-modal')
    },
    async saveAssign() {
      const fd = this.assignDriver
      const isCustom = this.assignFlagKey === this.ASSIGN_CUSTOM
      const flag = isCustom ? null : fd.flags.find(f => f.key === this.assignFlagKey)
      const topic = isCustom ? this.assignCustomTopic.trim() : flag && flag.label
      if (!topic) return
      this.assignSaving = true
      const assignee = this.assignContactOptions.find(o => o.value === this.assignTo)
      try {
        await create_coaching_ticket(this.tenant, {
          driver_id: fd.driver.driver_id || null,
          driver_name: fd.driver.driver_name,
          topic,
          why: this.assignWhy.trim() || (flag ? flag.note : undefined),
          // a hand-typed topic is not a scorecard finding: it files as
          // manual with no metric snapshot, so the dashboard's source mix
          // and the ticket's numbers stay honest
          source: isCustom ? 'manual' : 'scorecard_kpi',
          // omitted when unset so the backend keeps defaulting to the actor
          assigned_to: assignee ? assignee.value : undefined,
          assigned_to_name: assignee ? assignee.text.replace(/ \(me\)$/, '') : undefined,
          metric_snapshot: flag ? [{
            metric: flag.key, label: flag.label, value: flag.value,
            severity: flag.severity, note: flag.note,
          }] : undefined,
          period: flag ? this.scorecardPeriodValue : undefined,
          due_date: this.assignDueDate || undefined,
        })
        this.$bvModal.hide('cs-assign-modal')
        const owner = assignee && assignee.value !== this.user.username
          ? ` and assigned to ${assignee.text}` : ''
        this.$bvToast.toast(
          `Coaching raised for ${fd.driver.driver_name}${owner} — it is now in the queue.`,
          { title: 'Coaching Portal', variant: 'success', autoHideDelay: 4000 },
        )
        this.loadTickets()
        if (this.afterTicketMutation) this.afterTicketMutation()
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, 'Could not create the coaching ticket.'),
          { title: 'Coaching Portal', variant: 'danger', autoHideDelay: 6000 },
        )
      } finally {
        this.assignSaving = false
      }
    },

    isRecognitionQueued(driver, topic) {
      return (!!driver.driver_id && this.openRecognitionKeys.has(`${driver.driver_id}#${topic}`))
        || (!!driver.driver_name && this.openRecognitionKeys.has(`${driver.driver_name}#${topic}`))
    },
    allReasonsQueued(rc) {
      return rc.reasons.every(r => this.isRecognitionQueued(rc.driver, r.label))
    },
    openRecognize(rc) {
      this.recognizeDriver = rc
      const firstFree = rc.reasons.find(r => !this.isRecognitionQueued(rc.driver, r.label)) || rc.reasons[0]
      this.recognizeReasonKey = firstFree ? firstFree.key : null
      this.recognizeWhy = firstFree ? firstFree.note : ''
      this.recognizeWhyPristine = this.recognizeWhy
      this.recognizeTo = this.user.username
      this.loadContacts()
      this.$bvModal.show('cs-recognize-modal')
    },
    async saveRecognize() {
      const rc = this.recognizeDriver
      const reason = rc.reasons.find(r => r.key === this.recognizeReasonKey)
      if (!reason) return
      this.recognizeSaving = true
      const assignee = this.assignContactOptions.find(o => o.value === this.recognizeTo)
      try {
        await create_coaching_ticket(this.tenant, {
          driver_id: rc.driver.driver_id || null,
          driver_name: rc.driver.driver_name,
          kind: 'recognition',
          topic: reason.label,
          why: this.recognizeWhy.trim() || reason.note,
          source: 'scorecard_kpi',
          // omitted when unset so the backend keeps defaulting to the actor
          assigned_to: assignee ? assignee.value : undefined,
          assigned_to_name: assignee ? assignee.text.replace(/ \(me\)$/, '') : undefined,
          metric_snapshot: [{
            metric: reason.key, label: reason.label, value: reason.value, note: reason.note,
          }],
          period: this.scorecardPeriodValue,
        })
        this.$bvModal.hide('cs-recognize-modal')
        const owner = assignee && assignee.value !== this.user.username
          ? `, assigned to ${assignee.text}` : ''
        this.$bvToast.toast(
          `${rc.driver.driver_name} added to the recognition lane${owner} — sign it off from Coaching Tickets once it's been delivered.`,
          { title: 'Coaching Portal', variant: 'success', autoHideDelay: 4000 },
        )
        this.loadTickets()
        if (this.afterTicketMutation) this.afterTicketMutation()
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, 'Could not create the recognition record.'),
          { title: 'Coaching Portal', variant: 'danger', autoHideDelay: 6000 },
        )
      } finally {
        this.recognizeSaving = false
      }
    },
  },
}
